import React, { useState, useEffect, useCallback } from 'react';
import { NavigationContainer, useFocusEffect, useNavigation } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { StatusBar } from 'expo-status-bar';
import { Platform, View, Text, Image, Dimensions, SafeAreaView, Linking } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { HeroOutline } from '@nandorojo/heroicons';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Buffer } from 'buffer';

// Import screens
import LoginScreen from './src/screens/LoginScreen';
import SplashScreen from './src/screens/SplashScreen';
import RegisterScreen from './src/screens/RegisterScreen';
import ForgotPasswordScreen from './src/screens/ForgotPasswordScreen';
import ResetPasswordScreen from './src/screens/ResetPasswordScreen';
import HomeScreen from './src/screens/HomeScreen';
import CouponsScreen from './src/screens/CouponsScreen';
import ScanScreen from './src/screens/ScanScreen';
import TukarPoinCatalogScreen from './src/screens/TukarPoinCatalogScreen';
import TukarPoinDetailScreen from './src/screens/TukarPoinDetailScreen';
import TukarPoinShippingScreen from './src/screens/TukarPoinShippingScreen';
import TukarPoinHistoryScreen from './src/screens/TukarPoinHistoryScreen';
import PointsScreen from './src/screens/PointsScreen';
import PointExpiryScreen from './src/screens/PointExpiryScreen';
import PointsTabScreen from './src/screens/PointsTabScreen';
import RewardsScreen from './src/screens/RewardsScreen';
import ProfileScreen from './src/screens/ProfileScreen';
import LoadingScreen from './src/screens/LoadingScreen';
import BrowseScreen from './src/screens/BrowseScreen';
import CouponDetailsScreen from './src/screens/CouponDetailsScreen';
import IndobestCouponDetailsScreen from './src/screens/IndobestCouponDetailsScreen';
import PaygiftCouponDetailsScreen from './src/screens/PaygiftCouponDetailsScreen';
import EstampScreen from './src/screens/EstampScreen';
import HistoryScreen from './src/screens/HistoryScreen';
import EditProfileScreen from './src/screens/EditProfileScreen';
import NotificationScreen from './src/screens/NotificationScreen';
import ReferralScreen from './src/screens/ReferralScreen';
import ShippingAddressScreen from './src/screens/ShippingAddressScreen';
import ShippingAddressesListScreen from './src/screens/ShippingAddressesListScreen';
import PreLoginScanScreen from './src/screens/PreLoginScanScreen';
import TokenLoginScreen from './src/screens/TokenLoginScreen';
import DeviceRewardScreen from './src/screens/DeviceRewardScreen';
import SpinningWheelScreen from './src/screens/SpinningWheelScreen';
import SpinPrizeClaimHistoryScreen from './src/screens/SpinPrizeClaimHistoryScreen';
import { FloatingButtonProvider } from './src/contexts/FloatingButtonContext';
import FloatingZoolyfeButton from './src/components/FloatingZoolyfeButton';
import VerificationScreen from './src/screens/VerificationScreen';
import IdentityUploadScreen from './src/screens/IdentityUploadScreen';
import PhoneAuthScreen from './src/screens/PhoneAuthScreen';
import PasswordCreateScreen from './src/screens/PasswordCreateScreen';
import ProfileFormScreen from './src/screens/ProfileFormScreen';
import PhoneFormScreen from './src/screens/PhoneFormScreen';
import ShowCouponScreen from './src/screens/ShowCouponScreen';

import { doLogout } from './src/services/userDataService';
import { loadSettings } from './src/services/systemApi';

import { getPendingQRs, addPendingQR, deletePendingQR, resetPendingQRs } from './src/utils/pendingQR';
import {
  extractSocialBannerIdFromUrl,
  hydratePendingSocialBannerId,
  persistSocialBannerIdFromUrl,
} from './src/utils/socialBannerLink';

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();

function extractSpinProgramCodeFromUrl(url) {
  if (!url || !String(url).includes('/spin-wheel/')) return null;
  const part = String(url).split('/spin-wheel/')[1] || '';
  const spinCode = decodeURIComponent(part.split(/[?#]/)[0] || '').trim();
  return spinCode || null;
}

function navigateToSocialBanner(navigationRef, bannerId) {
  if (!bannerId || !navigationRef?.current) return false;
  navigationRef.current.navigate('MainTabs', {
    screen: 'Home',
    params: {
      socialBannerId: bannerId,
      socialBannerNonce: Date.now(),
    },
  });
  return true;
}

function navigateToSpinFromQr(navigationRef, spinCode) {
  if (!spinCode || !navigationRef?.current) return false;
  navigationRef.current.navigate('MainTabs', {
    screen: 'Home',
    params: {
      openSpinFromQr: true,
      spinProgramCode: spinCode,
      spinQrScanNonce: Date.now(),
    },
  });
  return true;
}

/** Capture the first browser URL before React Navigation rewrites it to /Splash. */
const webBootUrl =
  typeof window !== 'undefined' && window.location?.href
    ? window.location.href
    : null;

// Bottom Tab Navigator
function MainTabNavigator() {
  return (
    <FloatingButtonProvider>
      <Tab.Navigator
      screenOptions={({ route }) => {        
        return {
        tabBarIcon: ({ focused, color, size }) => {
          let iconSource;
          let iconSize = 24;

          if (route.name === 'Home') {
            iconSource = focused 
              ? require('./assets/icons/home-icon-selected.png')
              : require('./assets/icons/home-icon-unselected.png');
          } else if (route.name === 'Coupons') {
            iconSource = focused 
              ? require('./assets/icons/ticket-icon-selected.png')
              : require('./assets/icons/ticket-icon-unselected.png');
          } else if (route.name === 'TukarPoin') {
            return (
              <View style={{
                alignItems: 'center',
                justifyContent: 'center',
                transform: [{ translateY: -24 }],
              }}>
                <LinearGradient
                  colors={['#F1C16E', '#D38A0F']}
                  start={{ x: 0.5, y: 0 }}
                  end={{ x: 0.5, y: 1 }}
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 28,
                    padding: 8,
                    justifyContent: 'center',
                    alignItems: 'center',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.18,
                    shadowRadius: 10,
                    elevation: 8,
                  }}
                >
                  {/*<HeroOutline.Star height={40} width={40} color="#FFFFFF" />*/}
                  <Image 
                    source={focused 
                      ? require('./assets/icons/z-point-solid.svg')
                      : require('./assets/icons/z-point-outline.svg')
                    } 
                    style={{ 
                      width: 40, 
                      height: 40, 
                      tintColor: "#FFFFFF"
                    }}
                    resizeMode="contain"
                  />
                </LinearGradient>
                <Text
                  numberOfLines={1}
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: 10,
                    color,
                    textAlign: 'center',
                    marginTop: 2,
                  }}
                >
                  Tukar Poin
                </Text>
              </View>
            );
          } else if (route.name === 'Estamps') {
            iconSource = focused 
              ? require('./assets/icons/star-icon-selected.png')
              : require('./assets/icons/star-icon.png');
          } else if (route.name === 'Profile') {
            iconSource = focused 
              ? require('./assets/icons/user-circle-icon-selected.png')
              : require('./assets/icons/user-circle-icon-unselected.png');
          }

          return (
            <Image 
              source={iconSource} 
              style={{ 
                width: iconSize, 
                height: iconSize, 
                tintColor: color 
              }}
              resizeMode="contain"
            />
          );
        },
        tabBarLabel: ({ focused, color }) => {
          let label;
          if (route.name === 'Home') {
            label = 'Beranda';
          } else if (route.name === 'Coupons') {
            label = 'Coupon';
          } else if (route.name === 'TukarPoin') {
            return <View style={{ height: 0 }} />;
          } else if (route.name === 'Estamps') {
            label = 'E-Stamp';
          } else if (route.name === 'Profile') {
            label = 'Akun';
          }
          
          return (
            <Text style={{
              fontFamily: 'Poppins',
              fontSize: 10,
              color: color,
              textAlign: 'center',
              marginTop: 2,
            }}>
              {label}
            </Text>
          );
        },
        tabBarActiveTintColor: '#1E293B',
        tabBarInactiveTintColor: '#94A3B8',
        headerShown: false,
        tabBarStyle: {
          backgroundColor: '#FFFFFF',
          borderRadius: 24,
          borderTopWidth: 0,
          paddingTop: 8,
          paddingBottom: Platform.OS === 'ios' ? 34 : 8,
          paddingHorizontal: 24,
          height: Platform.OS === 'ios' ? 97 : 73,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: -2 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 8,
          position: 'absolute',
          bottom: Platform.OS === 'ios' ? 20 : 16,
          marginHorizontal: 16,
          alignSelf: 'center',
          overflow: 'visible',
        },
        tabBarItemStyle: {
          paddingVertical: 10,
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          overflow: 'visible',
        },
      };
      }}
    >
      <Tab.Screen name="Home" component={HomeScreen} />
      <Tab.Screen name="Coupons" component={CouponsScreen} />
      <Tab.Screen name="TukarPoin" component={TukarPoinCatalogScreen} />
      <Tab.Screen name="Estamps" component={EstampScreen} />
      <Tab.Screen name="Profile" component={ProfileScreen} />
    </Tab.Navigator>
    <FloatingZoolyfeButton />
    </FloatingButtonProvider>
  );
}

// Auth Stack Navigator
function AuthStackNavigator() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Splash" component={SplashScreen} />
      <Stack.Screen name="Login" component={LoginScreen} />
      <Stack.Screen name="Register" component={RegisterScreen} />
      <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
      <Stack.Screen name="ResetPassword" component={ResetPasswordScreen} />
      <Stack.Screen name="Browse" component={BrowseScreen} />
      <Stack.Screen name="PreLoginScan" component={PreLoginScanScreen} />
      <Stack.Screen name="TokenLogin" component={TokenLoginScreen} />
      <Stack.Screen name="PhoneAuth" component={PhoneAuthScreen} />
      <Stack.Screen name="PasswordCreate" component={PasswordCreateScreen} />
    </Stack.Navigator>
  );
}

// Main Stack Navigator that includes tabs and additional screens
function MainStackNavigator() {
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        <Stack.Screen name="MainTabs" component={MainTabNavigator} />
        <Stack.Screen name="Scan" component={ScanScreen} />
        <Stack.Screen name="TukarPoinDetail" component={TukarPoinDetailScreen} />
        <Stack.Screen name="TukarPoinShipping" component={TukarPoinShippingScreen} />
        <Stack.Screen name="TukarPoinHistory" component={TukarPoinHistoryScreen} />
        <Stack.Screen name="Points" component={PointsScreen} />
        <Stack.Screen name="PointExpiry" component={PointExpiryScreen} />
        <Stack.Screen name="CouponDetails" component={CouponDetailsScreen} />
        <Stack.Screen name="IndobestCouponDetails" component={IndobestCouponDetailsScreen} />
        <Stack.Screen name="PaygiftCouponDetails" component={PaygiftCouponDetailsScreen} />
        <Stack.Screen name="Estamp" component={EstampScreen} />
        <Stack.Screen name="History" component={HistoryScreen} />
        <Stack.Screen name="EditProfile" component={EditProfileScreen} />
        <Stack.Screen name="Notification" component={NotificationScreen} />
        <Stack.Screen name="DeviceReward" component={DeviceRewardScreen} />
        <Stack.Screen name="Referral" component={ReferralScreen} />
        <Stack.Screen name="ShippingAddressesList" component={ShippingAddressesListScreen} />
        <Stack.Screen name="ShippingAddress" component={ShippingAddressScreen} />
        <Stack.Screen name="SpinningWheel" component={SpinningWheelScreen} />
        <Stack.Screen name="SpinPrizeClaimHistory" component={SpinPrizeClaimHistoryScreen} />
        <Stack.Screen name="Verification" component={VerificationScreen} />
        <Stack.Screen name="IdentityUpload" component={IdentityUploadScreen} />
        <Stack.Screen name="ProfileForm" component={ProfileFormScreen} />
        <Stack.Screen name="PhoneForm" component={PhoneFormScreen} />
        <Stack.Screen name="ShowCoupon" component={ShowCouponScreen} />
      </Stack.Navigator>
    </SafeAreaView>
  );
}

// Navigation wrapper component that handles auth state
function NavigationWrapper({ initialUrl, navigationRef }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [shouldNavigateToLogin, setShouldNavigateToLogin] = useState(false);

  const checkAuthStatus = async () => {
    try {
      console.log('Checking auth status...');
      const pendingToken = await AsyncStorage.getItem('pending_login_token');
      // Always process new token — token is source of truth (fixes zoolife→hexpod→reward-claim wrong user)
      if (pendingToken && pendingToken.trim() !== '') {
        console.log('Pending SSO token found — showing AuthStack so TokenLogin processes (incl. device_reward)');
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      
      // console.log('Auth status check:', {
      //   userToken: userToken ? 'Present' : 'Missing',
      //   sessionId: sessionId ? 'Present' : 'Missing', 
      //   userTokenValue: userToken,
      //   sessionIdValue: sessionId
      // });
      
      // Reset states first
      setIsAuthenticated(false);
      
      // User is authenticated only if they have both userToken and session_id
      // This ensures API calls will work properly
      if (userToken && userToken.trim() !== '' && sessionId && sessionId.trim() !== '') {
        console.log('Setting authenticated to true - both token and session present');
        setIsAuthenticated(true);
      } else {
        console.log('No valid authentication found, staying logged out');
        // console.log('Missing:', {
        //   userToken: !userToken || userToken.trim() === '',
        //   sessionId: !sessionId || sessionId.trim() === ''
        // });
      }
    } catch (error) {
      console.log('Error checking auth status:', error);
      // On error, ensure we're logged out
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Handle QR code URL navigation
  useEffect(() => {
    if (initialUrl && initialUrl.includes('/qr/') && !isLoading) {
      console.log('QR code URL detected, setting navigation flag');
      setShouldNavigateToLogin(true);
    }
  }, [initialUrl, isLoading]);

  // Re-check auth status when app comes into focus
  useFocusEffect(
    useCallback(() => {
      console.log('useFocusEffect triggered, isLoading:', isLoading);
      if (!isLoading) {
        checkAuthStatus();
      }
    }, [isLoading])
  );

  // Add a global function to manually trigger auth check for web compatibility
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.forceAuthCheck = () => {
        console.log('Manual auth check triggered from window.forceAuthCheck');
        checkAuthStatus();
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.forceAuthCheck;
      }
    };
  }, []);

  // After auth is confirmed, check for pending device_reward / spin-wheel QR.
  // initialUrl is included so a spin-wheel deep link stored after the first auth check still opens.
  useEffect(() => {
    if(!isLoading) {
      if (isAuthenticated) {
        // checkAuthPendingQR();

        handlePendingShowCoupon();
        handlePendingDeviceReward();
        handlePendingSpinWheel();
        handlePendingSocialBanner();
      } else {
        handleGuestExternalAuthFailed();
        handleGuestPendingQR();
      }
    }
  }, [isLoading, isAuthenticated, initialUrl]);

  const handlePendingSpinWheel = async () => {
    try {
      const code = await AsyncStorage.getItem('pending_spin_program_code');
      if (!code || !navigationRef?.current) return;
      await AsyncStorage.removeItem('pending_spin_program_code');
      setTimeout(() => {
        navigateToSpinFromQr(navigationRef, code);
      }, 500);
    } catch (e) {
      console.log('handlePendingSpinWheel:', e?.message || e);
    }
  };

  const handlePendingSocialBanner = async () => {
    try {
      const bannerId = await hydratePendingSocialBannerId();
      if (!bannerId || !navigationRef?.current) return;
      navigateToSocialBanner(navigationRef, bannerId);
    } catch (e) {
      console.log('handlePendingSocialBanner:', e?.message || e);
    }
  };

  const handlePendingDeviceReward = async () => {
    try {
      const action      = await AsyncStorage.getItem('pending_action');
      const rewardToken = await AsyncStorage.getItem('pending_reward_token');
      const hexpodUrl   = await AsyncStorage.getItem('pending_hexpod_url');

      if (action === 'device_reward' && rewardToken) {
        // Consume the pending params so they're not re-triggered
        await AsyncStorage.multiRemove(['pending_action', 'pending_reward_token', 'pending_hexpod_url']);
        console.log('Pending device reward found, navigating to DeviceRewardScreen');

        // Small delay to ensure MainStackNavigator is fully rendered
        setTimeout(() => {
          if (navigationRef?.current) {
            navigationRef.current.navigate('DeviceReward', {
              reward_token: rewardToken,
              hexpod_url:   hexpodUrl || '',
              reward_flow_nonce: Date.now(),
            });
          }
        }, 400);
      }
    } catch (e) {
      console.log('Error handling pending device reward:', e);
    }
  };

  const handlePendingShowCoupon = async () => {
    try {
      const action      = await AsyncStorage.getItem('pending_action');
      const couponId    = await AsyncStorage.getItem('pending_coupon_id');

      if (action === 'show_coupon' && couponId) {
        // Consume the pending params so they're not re-triggered
        await AsyncStorage.multiRemove(['pending_action', 'pending_coupon_id']);
        console.log('Pending show coupon found, navigating to ShowCouponScreen');

        // Small delay to ensure MainStackNavigator is fully rendered
        setTimeout(() => {
          if (navigationRef?.current) {
            navigationRef.current.navigate('ShowCoupon', {
              coupon_id: couponId,
            });
          }
        }, 400);
      }
    } catch (e) {
      console.log('Error handling pending device reward:', e);
    }
  };

  const handleGuestPendingQR = async () => {
    try {
      const pendingQRs = await getPendingQRs();
      if(pendingQRs.length > 0) {
        // check if phone only auth allowed
        const systemSettings = await loadSettings([
          'CONF_SYSTEM_ALLOW_PHONE_ONLY'
        ]);
        if( systemSettings && systemSettings['CONF_SYSTEM_ALLOW_PHONE_ONLY'] === '1' ) {
          setTimeout(() => {
            if (navigationRef?.current) {
              navigationRef.current.navigate('PhoneAuth', {});
            }
          }, 400);
        }
      }
    } catch (e) {
      console.log('Error handling pending QR: ', e);
    }
  }

  const checkAuthPendingQR = async () => {
    try {
      const pendingQRs = await getPendingQRs();
      const userDataString = await AsyncStorage.getItem('userData');
      const userData = JSON.parse(userDataString);
      
      if( userData && !userData.user_email && pendingQRs.length == 0 ) {
        doLogout();
        return;
      }
    } catch (e) {
      console.log('Error handling pending QR: ', e);
    }
  }

  const handleGuestExternalAuthFailed = async () => {
    try {
      const authErrorMessage = await AsyncStorage.getItem('auth_error_message');
      if( !!authErrorMessage ) {
        const authCaller = await AsyncStorage.getItem('auth_caller');

        if( authCaller === "Login" ) {
          setTimeout(() => {
            if (navigationRef?.current) {
              navigationRef.current.navigate('Login', {});
            }
          }, 400);
        } else if( authCaller === "Register" ) {
          setTimeout(() => {
            if (navigationRef?.current) {
              navigationRef.current.navigate('Register', {});
            }
          }, 400);
        }
      }
    } catch (e) {
      console.log('Error handling failed external auth: ', e);
    }
  }

  // Remove periodic check to avoid interference with logout

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      {isAuthenticated ? (
        <MainStackNavigator />
      ) : (
        <AuthStackNavigator />
      )}
    </>
  );
}

// Deep linking configuration
const linking = {
  prefixes: [
    'zoolyfe-club://',
    'https://zoolyfe.club',
    'https://www.zoolyfe.club',
    'https://club.zoolyfe.id',
    'https://local-club.zoolyfe.id',
    'https://dev-club.zoolyfe.id',
  ],
  config: {
    screens: {
      // QR / spin-wheel / social banner URLs are handled in the useEffect below,
      // not through React Navigation linking.
    },
  },
};

export default function App() {
  const [initialUrl, setInitialUrl] = useState(null);
  const navigationRef = React.useRef();

  useEffect(() => {
    // Handle initial URL when app starts
    const getInitialUrl = async () => {
      try {
        let url = await Linking.getInitialURL();
        // Web fallback: getInitialURL often returns null; use window.location
        if (!url && typeof window !== 'undefined' && window.location?.href) {
          url = window.location.href;
        }
        // Prefer the URL at boot — NavigationContainer can rewrite /spin-wheel/:code or /social/:id to /Splash.
        if (
          webBootUrl &&
          (extractSpinProgramCodeFromUrl(webBootUrl) || extractSocialBannerIdFromUrl(webBootUrl))
        ) {
          url = webBootUrl;
        }
        if (url) {
          const spinCode = extractSpinProgramCodeFromUrl(url);
          if (spinCode) {
            await AsyncStorage.setItem('pending_spin_program_code', spinCode);
          }
          await persistSocialBannerIdFromUrl(url);
          setInitialUrl(url);
          console.log('Initial URL detected:', url);
          
          // Check if this is a QR code URL pattern
          if (url.includes('/qr/')) {
            const qrCode = url.split('/qr/')[1];
            if (qrCode) {
              console.log('QR code detected in URL:', qrCode);
              // Store the QR code for later redemption
              // await AsyncStorage.setItem('pending_qr_code', qrCode);
              await addPendingQR(qrCode);
              await AsyncStorage.removeItem('remindLater');
            }
          }

          // Check if this is a token login URL pattern
          if (url.includes('/login-with-token')) {
            console.log('Token login URL detected (initial):', url);
            
            // Parse URL parameters to extract token + optional reward params
            const urlObj = new URL(url);
            const token       = urlObj.searchParams.get('token');
            const backUrl     = urlObj.searchParams.get('back_url');
            const action      = urlObj.searchParams.get('action');
            const rewardToken = urlObj.searchParams.get('reward_token');
            const hexpodUrl   = urlObj.searchParams.get('hexpod_url');
            const couponId    = urlObj.searchParams.get('coupon_id');
            
            if (token) {
              console.log('Action:', action, '| Reward token:', rewardToken ? 'Present' : 'Missing');

              // Always store params — NavigationWrapper will pick them up after auth check
              await AsyncStorage.setItem('pending_login_token', token);
              if (backUrl)     await AsyncStorage.setItem('pending_back_url', backUrl);
              if (action)      await AsyncStorage.setItem('pending_action', action);
              if (rewardToken) await AsyncStorage.setItem('pending_reward_token', rewardToken);
              if (hexpodUrl)   await AsyncStorage.setItem('pending_hexpod_url', hexpodUrl);
              if (couponId)    await AsyncStorage.setItem('pending_coupon_id', couponId);

              // Check if user is already authenticated
              const existingToken   = await AsyncStorage.getItem('userToken');
              const existingSession = await AsyncStorage.getItem('session_id');
              const alreadyAuth = !!(existingToken?.trim() && existingSession?.trim());

              if (
                alreadyAuth 
                && ( 
                  action === 'device_reward'
                  || action === 'show_coupon'
                )
              ) {
                // Already logged in: trigger re-check so handlePendingDeviceReward runs (params now stored)
                console.log('Device reward — already authenticated, triggering handlePendingDeviceReward');
                if (typeof window !== 'undefined' && window.forceAuthCheck) {
                  window.forceAuthCheck();
                }
              } else {
                // Force AuthStack so TokenLogin can run (TokenLogin is in AuthStack, not MainStack)
                if (typeof window !== 'undefined' && window.forceAuthCheck) {
                  window.forceAuthCheck();
                }
              }
            } else {
              console.warn('No token found in login URL');
            }
          }

          if (url.includes('/external-auth')) {
            try {
              // Parse URL parameters to extract token + optional reward params
              const urlObj = new URL(url);
              const dataEncoded = urlObj.searchParams.get('data');
              if(!!dataEncoded) {
                const dataDecodedString = Buffer.from( dataEncoded, 'base64' ).toString('utf-8');
                const data = JSON.parse( dataDecodedString );

                if( !!data ) {
                  await AsyncStorage.setItem('userData', dataDecodedString);

                  // Store session data if provided
                  if (!!data.remember_token) {
                    await AsyncStorage.setItem('userToken', data.remember_token);
                  }
                  // Store session data if provided
                  if (!!data.session_id) {
                    await AsyncStorage.setItem('session_id', data.session_id);
                  }
                }
              }

              const error = urlObj.searchParams.get('error');
              if(!!error) {
                await AsyncStorage.setItem('auth_error_message', error);
              }
            } catch(e) {
              console.log( e );
            }

            window.forceAuthCheck();
          }
        }
      } catch (error) {
        console.log('Error getting initial URL:', error);
      }
    };

    getInitialUrl();

    // Listen for URL events while app is running
    const urlListener = Linking.addEventListener('url', async ({ url }) => {
      console.log('URL event received:', url);
      
      // Check if this is a QR code URL pattern
        if (url.includes('/qr/')) {
          const qrCode = url.split('/qr/')[1];
          if (qrCode) {
            console.log('QR code detected in URL event:', qrCode);
            // Store the QR code for later redemption
            // await AsyncStorage.setItem('pending_qr_code', qrCode);
            await addPendingQR(qrCode);
            await AsyncStorage.removeItem('remindLater');
          }
        }

        const spinCode = extractSpinProgramCodeFromUrl(url);
        if (spinCode) {
          await AsyncStorage.setItem('pending_spin_program_code', spinCode);
          const existingToken = await AsyncStorage.getItem('userToken');
          const existingSession = await AsyncStorage.getItem('session_id');
          if (existingToken?.trim() && existingSession?.trim()) {
            await AsyncStorage.removeItem('pending_spin_program_code');
            navigateToSpinFromQr(navigationRef, spinCode);
          }
        }

        await persistSocialBannerIdFromUrl(url);
        const existingTokenForSocial = await AsyncStorage.getItem('userToken');
        const existingSessionForSocial = await AsyncStorage.getItem('session_id');
        const socialBannerId = extractSocialBannerIdFromUrl(url);
        if (
          socialBannerId &&
          existingTokenForSocial?.trim() &&
          existingSessionForSocial?.trim()
        ) {
          navigateToSocialBanner(navigationRef, socialBannerId);
        }
        
        // Check if this is a token login URL pattern
        if (url.includes('/login-with-token')) {
          console.log('Token login URL detected (listener):', url);
          
          // Parse URL parameters to extract token + optional reward params
          const urlObj = new URL(url);
          const token       = urlObj.searchParams.get('token');
          const backUrl     = urlObj.searchParams.get('back_url');
          const action      = urlObj.searchParams.get('action');
          const rewardToken = urlObj.searchParams.get('reward_token');
          const hexpodUrl   = urlObj.searchParams.get('hexpod_url');
          
          if (token) {
            console.log('Token extracted (listener):', token ? 'Present' : 'Missing');
            console.log('Action:', action, '| Reward token:', rewardToken ? 'Present' : 'Missing');

            await AsyncStorage.setItem('pending_login_token', token);
            if (backUrl)     await AsyncStorage.setItem('pending_back_url', backUrl);
            if (action)      await AsyncStorage.setItem('pending_action', action);
            if (rewardToken) await AsyncStorage.setItem('pending_reward_token', rewardToken);
            if (hexpodUrl)   await AsyncStorage.setItem('pending_hexpod_url', hexpodUrl);

            // device_reward: trigger re-check so handlePendingDeviceReward runs
            // Other: trigger so AuthStack shows and SplashScreen navigates to TokenLogin
            if (typeof window !== 'undefined' && window.forceAuthCheck) {
              window.forceAuthCheck();
            }
          } else {
            console.warn('No token found in login URL');
          }
        }
    });

    return () => {
      urlListener.remove();
    };
  }, []);

  return (
    <>
      <StatusBar style="auto" />
      <View style={{
        flex: 1,
        backgroundColor: '#f5f5f5', // Light background for desktop
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <View style={{
          width: '100%',
          maxWidth: 430, // Mobile phone width (iPhone 14 Pro Max width)
          height: '100%',
          backgroundColor: '#fff',
          ...(Platform.OS === 'web' ? {
            boxShadow: '0 0 20px rgba(0,0,0,0.1)', // Subtle shadow on web
          } : {})
        }}>
          <NavigationContainer 
            linking={linking}
            ref={navigationRef}
          >
            <NavigationWrapper initialUrl={initialUrl} navigationRef={navigationRef} />
          </NavigationContainer>
        </View>
      </View>
    </>
  );
}